import { Subject } from 'rxjs';
import { CoordinatorService } from './coordinator.service';
import { MyWorker } from './worker';
declare const showOpenFilePicker: any;
declare const CompressionStream: any;

type STARTUP_FILENAME = '_pcode';

type RF = { file: File; payload: any };
export class Filesystem$ {
    response$ = new Subject<any>();
    templateMapChange$ = new Subject();
    loadFinished$ = new Subject();

    constructor(private coords: CoordinatorService) {}

    async setStartupFile(fn: STARTUP_FILENAME, payload: any) {
        return this.coords.utils$.writeToDisk(`_startup/${fn}`, JSON.stringify(payload));
    }

    async getStartupFile(
        fn: STARTUP_FILENAME,
        { expireSeconds, deleteAfterRead }: { expireSeconds?: number; deleteAfterRead?: boolean },
    ) {
        try {
            let dH = await navigator.storage.getDirectory();
            dH = await dH.getDirectoryHandle('_startup');
            const fH = await dH.getFileHandle(fn);
            const f = await fH.getFile();
            if (expireSeconds) {
                const currentTime = Date.now();
                const ageInSeconds = Math.floor((currentTime - f.lastModified) / 1000);

                if (ageInSeconds >= expireSeconds) {
                    deleteAfterRead = true;
                    return undefined;
                }
            }
            const resp = JSON.parse(await f.text());
            deleteAfterRead && dH.removeEntry(fH.name);
            return resp;
        } catch (e) {}
    }
    async drop(e: DragEvent) {
        return Promise.all(Array.from(e!.dataTransfer!.files).map((x) => this.readFile(x)));
    }

    async readJSONFile(dH: FileSystemDirectoryHandle, fn: string) {
        const fH = await dH.getFileHandle(fn);
        return await this.readJSONFileFromFilehandle(fH);
    }

    async readJSONFileFromFilehandle(fH: FileSystemFileHandle) {
        try {
            const f = await fH.getFile();
            return JSON.parse(await f.text());
        } catch (e) {
            console.error('readJSONFileFromFilehandle', fH, e);
        }
    }

    fileOrBlobToDataUrl(fileOrBlob: Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onloadend = () => resolve(reader.result as string); // Success: Data URL as result
            reader.onerror = reject; // Error handler

            reader.readAsDataURL(fileOrBlob); // Start reading as Data URL
        });
    }

    async pickFileToDataURL() {
        const f = await this.coords.filesystem$.pickFiles(false);
        if (!f) return {};
        const data = await this.fileOrBlobToDataUrl(f);
        return {
            data,
            type: f.type,
        };
    }

    async readFile(f: File): Promise<any> {
        return new Promise((resolve, reject) => {
            var fr = new FileReader();
            fr.onload = () => {
                resolve({ payload: fr.result, file: f });
            };
            fr.onerror = reject;
            fr.readAsText(f);
        });
    }

    private async _compress(data: string) {
        const encoder = new TextEncoder();
        const compressedStream = new CompressionStream('gzip');
        const writableStream = compressedStream.writable;
        const readableStream = compressedStream.readable;

        const writer = writableStream.getWriter();
        writer.write(encoder.encode(data));
        writer.close();

        return new Response(readableStream).arrayBuffer();
    }

    async saveImportFile(filename: string, type: string, payload: any) {
        const bArr = await this._compress(JSON.stringify(payload));
        const bodyBuffer = new Uint8Array(bArr);
        const finalBuffer = new Uint8Array(200 + bodyBuffer.length);

        const params: { type: string; size?: number } = {
            type: type,
        };
        switch (type) {
            case 'xitem-list':
                params.size = payload.length;
                break;
        }

        const head = 'application/x-memowl' + JSON.stringify(params);
        const encoder = new TextEncoder();
        finalBuffer.set(encoder.encode(head), 0);
        finalBuffer.set(bodyBuffer, 200);

        this.saveFile2(filename, new Blob([finalBuffer], { type: 'application/octet-stream' }));
    }

    async parserLoad(filetype?: string) {
        return this.load(true, this.coords.worker$.parser, filetype);
    }

    async pickJsonFile() {
        const f = await this.pickFiles();
        try {
            const fData = await this.coords.filesystem$.readFile(f);
            return JSON.parse(fData.payload);
        } catch (e) {
            return f;
        }
    }

    async pickFiles(isMultiple = false) {
        if (0 && showOpenFilePicker()) {
            return showOpenFilePicker({
                multiple: isMultiple,
            });
        } else {
            return new Promise((resolve, reject) => {
                const hiddenElement = document.createElement('input');
                hiddenElement.type = 'file';
                hiddenElement.multiple = isMultiple;

                hiddenElement.onchange = (e) => {
                    const fLs = hiddenElement.files || new FileList();
                    if (!fLs.length) return reject(new Error('No files selected'));
                    resolve(isMultiple ? fLs : fLs[0]);
                };
                hiddenElement.click();
                hiddenElement.remove();
            });
        }
    }

    async load(
        isMultiple = true,
        worker: MyWorker | undefined = undefined,
        filetype: string | undefined = undefined,
    ): Promise<RF[]> {
        return new Promise((resolve, reject) => {
            const hiddenElement = document.createElement('input');
            hiddenElement.type = 'file';
            hiddenElement.multiple = isMultiple;
            hiddenElement.onchange = (e) => {
                if (worker) {
                    worker.directPost({
                        action: 'load',
                        payload: {
                            fileLs: hiddenElement.files,
                            filetype: filetype,
                        },
                    });
                    return;
                }
                let fileLs: any = hiddenElement.files;
                // if (!fileLs)
                //     fileLs = e['path'] && e['path'][0] || e['originalTarget']
                resolve(Promise.all(Array.from(fileLs).map((x: any) => this.readFile(x))));
                //   this.readData(inp.files, params)
            };
            hiddenElement.click();
            hiddenElement.remove();
        });
    }

    saveFile2(filename: string, blob: Blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    save(params: any) {

        let filename = params.filename;
        if (!filename) filename = params.rawFilename || 'export';
        filename = filename.trim().replace(/\s+/g, '_').toLowerCase();

        const payload = params['payload'];
        const isRaw = params['isRaw'];

        const hidElId = '_saveDialog';
        const el = document.getElementById(hidElId);
        if (el) {
            el.remove();
        }
        const hiddenElement = document.createElement('a');
        hiddenElement.id = hidElId;
        let type;
        let data;
        if (isRaw) {
            data = payload;
            if (filename.endsWith('csv')) {
                type = 'application/csv';
            } else if (filename.endsWith('.html')) {
                type = 'text/html';
            } else if (filename.endsWith('.xml')) {
                type = 'text/xml';
            } else {
                type = 'text/plain';
            }
        } else {
            data = JSON.stringify(payload);
            type = 'application/json';
        }

        const url = URL.createObjectURL(new Blob([data], { type: type }));
        hiddenElement.href = url;
        hiddenElement.target = '_blank';
        if (!isRaw) {
            filename += '.json';
        }
        hiddenElement.download = filename;
        hiddenElement.click();
        hiddenElement.remove();
        URL.revokeObjectURL(url);
    }

    async cacheDirLs(dH: FileSystemDirectoryHandle) {
        const mp = new Map<string, any>();
        for await (const e of (dH as any).values() as Iterable<FileSystemHandle>) {
            try {
                if (e.kind === 'directory') continue;
                let tag: string = e.name;
                let hash = '';
                if (tag.startsWith('.hash')) {
                    const i = e.name.lastIndexOf('_');
                    hash = tag.slice(i + 1);
                    tag = tag.slice(6, i);
                } else if (tag.startsWith('.')) continue;
                let obj: any = mp.get(tag);
                if (!obj) {
                    obj = {};
                    mp.set(tag, obj);
                }
                if (hash) {
                    obj.hash = hash;
                    obj.hashName = e.name;
                } else obj.fH = e;
            } catch (e) {}
        }

        const ls: {
            hash: string;
            hashName: string;
            fH: FileSystemFileHandle;
        }[] = [];
        for (const x of mp.values()) {
            try {
                if (x.fH && x.hash) {
                    ls.push(x);
                } else {
                    x.fH && (await dH.removeEntry(x.fH.name));
                    x.hash && (await dH.removeEntry(x.hashName));
                }
            } catch (e) {}
        }

        return ls;
    }
}
